import { useEffect, useMemo, useState } from 'react';
import { Layer, Marker, Source, useMap } from 'react-map-gl/mapbox';

import mapScreenUrl from '@/assets/icons/map-screen.svg';
import {
  getScreenGroupRadius,
  SCREEN_CLUSTER_COLOR,
  SCREEN_ICON_MAX_SIZE,
  SCREEN_IMAGE_ID,
  SCREENS_CLUSTER_PROPERTIES,
  SCREENS_SOURCE_ID,
  screenBackgroundLayer,
  screenIconLayer,
  screensClusterBadgeLayer,
  screensClusterCountLayer,
  screensClusterIconLayer,
  screensClusterLayer
} from '../../layers.const';
import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { groupScreensByCoordinates } from '../../MultiFlightMap.helpers';
import { useMapImage } from '../../MultiFlightMap.hooks';
import { useMapPopover } from '../MapPopover/MapPopover.context';
import { PopOverOptions } from '../PopOverOptions/PopOverOptions';
import ScreensStackTooltip from './ScreensStackTooltip/ScreensStackTooltip';

import type { FeatureCollection } from 'geojson';

type Props = {
  mapId: string;
};

const TOP_POSITION = 42;
const LEFT_POSITION = 20;
// Above the Source's clusterMaxZoom (14) screens are never clustered, so stacks are drawn on their own.
const STACKS_MIN_ZOOM = 15;

function ScreensLayer({ mapId }: Props) {
  const { temporaryScreen, temporaryScreens } = useMultiFlightMap();
  const { openMapPopover } = useMapPopover();
  const { [mapId]: map } = useMap();
  const [zoom, setZoom] = useState(0);

  const screensByCoordinates = useMemo(() => groupScreensByCoordinates(temporaryScreens), [temporaryScreens]);
  const stacks = useMemo(
    () => Array.from(screensByCoordinates.values()).filter(screens => screens.length > 1),
    [screensByCoordinates]
  );

  // One feature per coordinate: a single screen (count 1) or a stack of screens at exactly
  // the same spot (count > 1, drawn like a cluster and listed in a tooltip on click).
  const screensFeatures = useMemo<FeatureCollection>(() => {
    return {
      features: Array.from(screensByCoordinates, ([, screens]) => ({
        geometry: { coordinates: [screens[0].longitude!, screens[0].latitude!], type: 'Point' },
        properties: { count: screens.length, id: String(screens[0].source_element_id), kind: 'screen' },
        type: 'Feature'
      })),
      type: 'FeatureCollection'
    };
  }, [screensByCoordinates]);

  useEffect(() => {
    if (!map) {
      return;
    }
    const updateZoom = () => setZoom(map.getZoom());
    updateZoom();
    map.on('zoomend', updateZoom);
    return () => {
      map.off('zoomend', updateZoom);
    };
  }, [map]);

  useMapImage({
    color: SCREEN_CLUSTER_COLOR,
    height: 64,
    imageId: SCREEN_IMAGE_ID,
    mapId,
    maxIconSize: SCREEN_ICON_MAX_SIZE,
    url: mapScreenUrl,
    width: 64
  });

  useEffect(() => {
    if (!map || !temporaryScreen) {
      return;
    }

    const runPopoverSetup = async () => {
      try {
        if (temporaryScreen) {
          const { x, y } = map.project(temporaryScreen.polygon.coordinates);
          const mapContainer = map.getContainer();
          const rect = mapContainer.getBoundingClientRect();
          const adjustedX = x + rect.left;
          const adjustedY = y + rect.top;

          openMapPopover({
            render: () => <PopOverOptions optionsType="screens" />,
            x: adjustedX + LEFT_POSITION,
            y: adjustedY - TOP_POSITION
          });
        }
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: off
        console.error('Error setting up map popover:', error);
      }
    };

    runPopoverSetup();
  }, [map, temporaryScreen]);

  return (
    <>
      <Source
        cluster
        clusterMaxZoom={14}
        clusterProperties={SCREENS_CLUSTER_PROPERTIES}
        clusterRadius={60}
        data={screensFeatures}
        id={SCREENS_SOURCE_ID}
        type="geojson"
      >
        <Layer {...screensClusterLayer} />
        <Layer {...screensClusterIconLayer} />
        <Layer {...screensClusterBadgeLayer} />
        <Layer {...screensClusterCountLayer} />
        <Layer {...screenBackgroundLayer} />
        <Layer {...screenIconLayer} />
      </Source>
      {zoom >= STACKS_MIN_ZOOM &&
        stacks.map(screens => (
          <Marker
            key={String(screens[0].source_element_id)}
            latitude={screens[0].latitude!}
            longitude={screens[0].longitude!}
          >
            <ScreensStackTooltip screens={screens} size={getScreenGroupRadius(zoom) * 2} />
          </Marker>
        ))}
      {temporaryScreen && (
        <Marker draggable={false} latitude={temporaryScreen.latitude!} longitude={temporaryScreen.longitude!}>
          <div className="multiFlightViewMap__temporaryScreenContainer multiFlightViewMap__temporaryScreenContainer--unconfirmed">
            <i className="fa-kit fa-screen multiFlightViewMap__temporaryScreen" />
          </div>
        </Marker>
      )}
    </>
  );
}

export default ScreensLayer;
